import { Badge } from "@/components/ui/badge"
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from "@/components/ui/breadcrumb"
import { Button } from "@/components/ui/button"
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table"
import AppLayout from "@/layouts/AppLayout"
import { Head, router, usePage } from "@inertiajs/react"
import { useState } from "react"

interface ProjectRow {
  id: number
  name: string
  customer: { id: number; name: string }
  project_type: string
  total_revenue: number
  total_costs: number
  total_labor_cost: number
  gross_profit: number
  gross_margin_percentage: number | null
  headcount: number
  billable_hours: number
}

interface CustomerTotal {
  customer: { id: number; name: string }
  total_revenue: number
  total_costs: number
  total_labor_cost: number
  gross_profit: number
  gross_margin_percentage: number | null
  headcount: number
  project_count: number
}

interface Props {
  [key: string]: unknown
  filters: { start_date: string; end_date: string }
  projects: ProjectRow[]
  customer_totals: CustomerTotal[]
}

const fmt = (n: number) => n.toLocaleString("en-US", { style: "currency", currency: "USD" })
const fmtPct = (n: number | null) => (n != null ? `${n}%` : "—")

// Color-code gross margin tiers. Thresholds picked to match how the team thinks about
// project health: <0 broken, 0-20 thin, 20-40 ok, 40-60 strong, 60+ exceptional.
function marginToneClass(pct: number | null): string {
  if (pct == null) return "text-muted-foreground"
  if (pct < 0) return "text-red-600 dark:text-red-400 font-semibold"
  if (pct < 20) return "text-amber-600 dark:text-amber-400"
  if (pct < 40) return "text-foreground"
  if (pct < 60) return "text-emerald-600 dark:text-emerald-400"
  return "text-emerald-700 dark:text-emerald-300 font-semibold"
}

export default function ProjectFinanceReportsIndex() {
  const { filters, projects, customer_totals } = usePage<Props>().props
  const [startDate, setStartDate] = useState(filters.start_date)
  const [endDate, setEndDate] = useState(filters.end_date)

  function applyFilters() {
    router.get("/admin/project_finance_reports", { start_date: startDate, end_date: endDate }, { preserveScroll: true })
  }

  return (
    <>
      <Head title="Project P&L - Operations Center" />
      <div className="flex flex-1 flex-col gap-6 p-4 lg:p-6">
        <Breadcrumb>
          <BreadcrumbList>
            <BreadcrumbItem>
              <BreadcrumbLink href="/">Home</BreadcrumbLink>
            </BreadcrumbItem>
            <BreadcrumbSeparator />
            <BreadcrumbItem>
              <BreadcrumbPage>Project P&amp;L</BreadcrumbPage>
            </BreadcrumbItem>
          </BreadcrumbList>
        </Breadcrumb>

        <div className="flex items-end gap-3">
          <div className="space-y-1">
            <Label>From</Label>
            <Input type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)} />
          </div>
          <div className="space-y-1">
            <Label>To</Label>
            <Input type="date" value={endDate} onChange={(e) => setEndDate(e.target.value)} />
          </div>
          <Button onClick={applyFilters}>Apply</Button>
        </div>

        <Card>
          <CardHeader>
            <CardTitle>By customer</CardTitle>
          </CardHeader>
          <CardContent>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Customer</TableHead>
                  <TableHead className="text-right">Projects</TableHead>
                  <TableHead className="text-right">Headcount</TableHead>
                  <TableHead className="text-right">Total Revenue ($)</TableHead>
                  <TableHead className="text-right">Total Costs ($)</TableHead>
                  <TableHead className="text-right">Total Labor Cost ($)</TableHead>
                  <TableHead className="text-right">Gross Profit ($)</TableHead>
                  <TableHead className="text-right">Gross Margin %</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {customer_totals.map((c) => (
                  <TableRow key={c.customer.id}>
                    <TableCell className="font-medium">{c.customer.name}</TableCell>
                    <TableCell className="text-right tabular-nums">{c.project_count}</TableCell>
                    <TableCell className="text-right tabular-nums">{c.headcount}</TableCell>
                    <TableCell className="text-right tabular-nums">{fmt(c.total_revenue)}</TableCell>
                    <TableCell className="text-right tabular-nums">{fmt(c.total_costs)}</TableCell>
                    <TableCell className="text-right tabular-nums">{fmt(c.total_labor_cost)}</TableCell>
                    <TableCell className="text-right tabular-nums">{fmt(c.gross_profit)}</TableCell>
                    <TableCell className={`text-right tabular-nums ${marginToneClass(c.gross_margin_percentage)}`}>
                      {fmtPct(c.gross_margin_percentage)}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>By project</CardTitle>
          </CardHeader>
          <CardContent>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Project</TableHead>
                  <TableHead>Customer</TableHead>
                  <TableHead>Type</TableHead>
                  <TableHead className="text-right">Headcount</TableHead>
                  <TableHead className="text-right">Hours</TableHead>
                  <TableHead className="text-right">Total Revenue ($)</TableHead>
                  <TableHead className="text-right">Total Costs ($)</TableHead>
                  <TableHead className="text-right">Total Labor Cost ($)</TableHead>
                  <TableHead className="text-right">Gross Profit ($)</TableHead>
                  <TableHead className="text-right">Gross Margin %</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {projects.map((p) => (
                  <TableRow key={p.id}>
                    <TableCell>
                      <a
                        href={`/admin/project_finance_reports/${p.id}?start_date=${filters.start_date}&end_date=${filters.end_date}`}
                        className="text-primary hover:underline font-medium"
                      >
                        {p.name}
                      </a>
                    </TableCell>
                    <TableCell>{p.customer.name}</TableCell>
                    <TableCell>
                      <Badge variant="outline">{p.project_type === "fixed_price" ? "Fixed" : "T&M"}</Badge>
                    </TableCell>
                    <TableCell className="text-right tabular-nums">{p.headcount}</TableCell>
                    <TableCell className="text-right tabular-nums">{p.billable_hours}</TableCell>
                    <TableCell className="text-right tabular-nums">{fmt(p.total_revenue)}</TableCell>
                    <TableCell className="text-right tabular-nums">{fmt(p.total_costs)}</TableCell>
                    <TableCell className="text-right tabular-nums">{fmt(p.total_labor_cost)}</TableCell>
                    <TableCell className="text-right tabular-nums">{fmt(p.gross_profit)}</TableCell>
                    <TableCell className={`text-right tabular-nums ${marginToneClass(p.gross_margin_percentage)}`}>
                      {fmtPct(p.gross_margin_percentage)}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </CardContent>
        </Card>

        <p className="text-xs text-muted-foreground">
          Margin colors: <span className="text-red-600 dark:text-red-400 font-semibold">&lt; 0% loss</span> ·{" "}
          <span className="text-amber-600 dark:text-amber-400">0-20% thin</span> · 20-40% ok ·{" "}
          <span className="text-emerald-600 dark:text-emerald-400">40-60% strong</span> ·{" "}
          <span className="text-emerald-700 dark:text-emerald-300 font-semibold">60%+ exceptional</span>
        </p>
      </div>
    </>
  )
}

ProjectFinanceReportsIndex.layout = (page: React.ReactNode) => <AppLayout>{page}</AppLayout>
